/**
 * Google Analytics 4 (GA4) Service
 * Provides utility functions for tracking custom events throughout the application
 * All functions include production checks and safety validations
 */

/**
 * Check if Google Analytics is available and initialized
 * @returns {boolean} True if gtag is available
 */
const isGAAvailable = () => {
  return (import.meta.env?.MODE === 'production' && 
  typeof window !== 'undefined' && typeof window.gtag === 'function');
};

/**
 * Track custom events in Google Analytics
 * @param {string} eventName - Name of the event (e.g., 'button_click', 'form_submit')
 * @param {Object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGAAvailable()) return;

  try {
    window.gtag('event', eventName, {
      ...eventParams,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString(),
    });
    console.log('[GA4] Event tracked:', eventName, eventParams);
  } catch (error) {
    console.error('[GA4] Error tracking event:', error);
  }
};

/**
 * Track user authentication events
 * @param {string} action - Authentication action ('login', 'logout', 'signup')
 * @param {string} method - Authentication method ('email', 'google', 'github', etc.)
 * @param {Object} additionalParams - Additional parameters
 */
export const trackAuth = (action, method = 'email', additionalParams = {}) => {
  if (!isGAAvailable()) return;

  try {
    window.gtag('event', action, {
      method,
      ...additionalParams,
    });
    console.log('[GA4] Auth event tracked:', action, method);
  } catch (error) {
    console.error('[GA4] Error tracking auth event:', error);
  }
};

/**
 * Track button clicks with detailed context
 * @param {string} buttonId - Unique identifier for the button
 * @param {string} buttonText - Text displayed on the button
 * @param {Object} additionalParams - Additional context parameters
 */
export const trackButtonClick = (buttonId, buttonText = '', additionalParams = {}) => {
  trackEvent('button_click', {
    button_id: buttonId,
    button_text: buttonText,
    ...additionalParams,
  });
};

/**
 * Track form submissions
 * @param {string} formId - Unique identifier for the form
 * @param {string} formType - Type of form (e.g., 'contact', 'registration', 'trip_planning')
 * @param {Object} additionalParams - Additional form context
 */
export const trackFormSubmit = (formId, formType = '', additionalParams = {}) => {
  trackEvent('form_submit', {
    form_id: formId,
    form_type: formType,
    ...additionalParams,
  });
};

/**
 * Track navigation events (link clicks, menu interactions)
 * @param {string} destination - Where the user is navigating to
 * @param {string} linkText - Text of the link clicked
 * @param {Object} additionalParams - Additional navigation context
 */
export const trackNavigation = (destination, linkText = '', additionalParams = {}) => {
  trackEvent('navigation', {
    destination,
    link_text: linkText,
    ...additionalParams,
  });
};

/**
 * Track trip-related actions (creation, editing, deletion, sharing)
 * @param {string} action - Trip action ('create', 'edit', 'delete', 'share', 'view')
 * @param {string} tripId - Unique identifier for the trip
 * @param {Object} additionalParams - Additional trip context
 */
export const trackTripAction = (action, tripId = '', additionalParams = {}) => {
  trackEvent('trip_action', {
    action,
    trip_id: tripId,
    ...additionalParams,
  });
};

/**
 * Track search actions
 * @param {string} searchTerm - The search query entered by user
 * @param {number} resultsCount - Number of results returned
 * @param {Object} additionalParams - Additional search context
 */
export const trackSearch = (searchTerm, resultsCount = 0, additionalParams = {}) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
    ...additionalParams,
  });
};

/**
 * Track user engagement with features
 * @param {string} featureName - Name of the feature being used
 * @param {string} action - Specific action taken with the feature
 * @param {Object} additionalParams - Additional feature context
 */
export const trackFeatureUsage = (featureName, action = 'use', additionalParams = {}) => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    action,
    ...additionalParams,
  });
};

/**
 * Track errors and exceptions
 * @param {string} errorMessage - Error message
 * @param {string} errorLocation - Where the error occurred
 * @param {boolean} isFatal - Whether the error is fatal
 */
export const trackError = (errorMessage, errorLocation = '', isFatal = false) => {
  if (!isGAAvailable()) return;

  try {
    window.gtag('event', 'exception', {
      description: errorMessage,
      location: errorLocation,
      fatal: isFatal,
    });
    console.log('[GA4] Error tracked:', errorMessage);
  } catch (error) {
    console.error('[GA4] Error tracking error event:', error);
  }
};

/**
 * Set user properties for analytics segmentation
 * @param {Object} userProperties - Properties to associate with the user
 */
export const setUserProperties = (userProperties = {}) => {
  if (!isGAAvailable()) return;

  try {
    window.gtag('set', 'user_properties', userProperties);
    console.log('[GA4] User properties set:', userProperties);
  } catch (error) {
    console.error('[GA4] Error setting user properties:', error);
  }
};

/**
 * Track file downloads
 * @param {string} fileName - Name of the downloaded file
 * @param {string} fileType - Type/extension of the file (e.g., 'pdf', 'csv')
 */
export const trackDownload = (fileName, fileType = '') => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
  });
};

/**
 * Track social sharing actions
 * @param {string} platform - Social platform (e.g., 'facebook', 'twitter', 'email')
 * @param {string} contentType - Type of content shared (e.g., 'trip', 'article')
 * @param {string} contentId - ID of the shared content
 */
export const trackShare = (platform, contentType = '', contentId = '') => {
  trackEvent('share', {
    method: platform,
    content_type: contentType,
    content_id: contentId,
  });
};

export default {
  trackEvent,
  trackAuth,
  trackButtonClick,
  trackFormSubmit,
  trackNavigation,
  trackTripAction,
  trackSearch,
  trackFeatureUsage,
  trackError,
  setUserProperties,
  trackDownload,
  trackShare,
};